import { Card, Typography, Grid, Table, TableBody, TableCell, TableHead, TableRow, TableContainer, Link } from '@mui/material';
import ReactApexChart from 'react-apexcharts';
import formatMoney from 'src/utils/formatMoney';
import { getStageDisplayName } from 'src/utils/stageDisplay';

// Avg time-in-stage + stuck-deal risk tile + BM/BA owner rollups.
export default function VelocityRisk({ velocity, currencyId, currencyLabel, openDrill }) {
  if (!velocity) return null;
  const timeInStage = velocity.avg_time_in_stage || [];
  const stuck = velocity.stuck_deals || {};
  const byBm = velocity.owner_rollup?.by_bm || [];
  const byBa = velocity.owner_rollup?.by_ba || [];

  const chart = {
    series: [{ name: 'Avg days', data: timeInStage.map((s) => s.avg_days || 0) }],
    options: {
      chart: { type: 'bar', toolbar: { show: false }, height: 300 },
      plotOptions: { bar: { horizontal: true, borderRadius: 4, distributed: true } },
      xaxis: { categories: timeInStage.map((s) => getStageDisplayName(s.stage_label)) },
      grid: { borderColor: '#eee' },
      dataLabels: { enabled: true, formatter: (v) => `${v}d` },
      legend: { show: false },
      tooltip: { y: { formatter: (v) => `${v} days` } },
    },
  };

  const ownerDrill = (role, row, withCurrency) => {
    const params = {
      owner_role: role,
      owner_user_id: row.user_id === null || row.user_id === undefined ? 'null' : row.user_id,
      lifecycle: 'open',
    };
    if (withCurrency) params.currency_id = currencyId;
    openDrill(params, `${row.name} — ${role.toUpperCase()}`);
  };

  const ownerTable = (title, rows, role) => (
    <>
      <Typography variant="subtitle2" fontWeight={600} mb={0.5}>{title}</Typography>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Owner</TableCell>
              <TableCell align="right">Count</TableCell>
              <TableCell align="right">TCV</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, idx) => (
              <TableRow key={`${role}-${row.user_id ?? 'null'}-${idx}`} hover>
                <TableCell>{row.name}</TableCell>
                <TableCell align="right">
                  <Link component="button" underline="hover" onClick={() => ownerDrill(role, row, false)}>
                    {row.count}
                  </Link>
                </TableCell>
                <TableCell align="right">
                  <Link component="button" underline="hover" onClick={() => ownerDrill(role, row, true)}>
                    {formatMoney(row.tcv, currencyLabel)}
                  </Link>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{ color: 'text.secondary', py: 2 }}>No data.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );

  return (
    <Card sx={{ p: 2, mb: 3 }}>
      <Typography variant="h6" fontWeight={600} mb={2}>Velocity & Risk</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Typography variant="subtitle2" fontWeight={600} mb={0.5}>Avg Time in Stage (days)</Typography>
          {timeInStage.length ? (
            <ReactApexChart options={chart.options} series={chart.series} type="bar" height={300} />
          ) : (
            <Typography variant="body2" color="text.secondary">No stage timing data.</Typography>
          )}
        </Grid>
        <Grid item xs={12} md={4}>
          <Card
            variant="outlined"
            onClick={() => openDrill({ lifecycle: 'stuck' }, 'Stuck deals')}
            sx={{ p: 2, cursor: 'pointer', bgcolor: 'warning.lighter', '&:hover': { boxShadow: 6 } }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}>
              Stuck Deals
            </Typography>
            <Typography variant="h3" fontWeight={700} color="warning.dark">{stuck.count ?? 0}</Typography>
            <Typography variant="body2" color="text.secondary">{formatMoney(stuck.tcv, currencyLabel)} at risk</Typography>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>{ownerTable('By Business Manager (BM)', byBm, 'bm')}</Grid>
        <Grid item xs={12} md={6}>{ownerTable('By Business Analyst (BA)', byBa, 'ba')}</Grid>
      </Grid>
    </Card>
  );
}
